import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { FixedSizeList as List } from "react-window";
import { TableModel as ITableModel, TableConfig, ColumnDef } from "./types";
import { RowAction, RowActionsMenu } from "./RowActions";
import { BiqlTableModel } from "./BiqlTableModel";
import { TableToolbar, ToolbarAction } from "./TableToolbar";
import { SaveChangesDialog } from "./SaveChangesDialog";
import { SaveResult } from "./ChangeTracker";
import { emptyValueLabel } from "./DataType";
import "./Table.scss";

/** What the NULL flip writes: the type's empty value on a NULL cell, otherwise NULL. */
function flipTarget(value: unknown, emptyValue: unknown): unknown {
  return value == null && emptyValue !== undefined ? emptyValue : null;
}

export type { ToolbarAction } from "./TableToolbar";

/** One row, as the enclosing Table renders it this pass. */
type RowRenderer = (props: {
  index: number;
  style: React.CSSProperties;
}) => JSX.Element;

/**
 * The row component react-window sees — one stable type for the life of the
 * module, so a row keeps its state (an open actions menu) across every
 * re-render. The renderer itself arrives per render as `data`.
 */
function TableRow({
  index,
  style,
  data,
}: {
  index: number;
  style: React.CSSProperties;
  data: RowRenderer;
}) {
  return data({ index, style });
}

const SELECT_COL_WIDTH = 36;
const selectCellStyle: React.CSSProperties = {
  width: SELECT_COL_WIDTH,
  minWidth: SELECT_COL_WIDTH,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  cursor: "pointer",
};

interface TableProps<T = unknown> {
  model: ITableModel<T>;
  config?: TableConfig;
  height?: number;
  width?: number;
  onRowClick?: (row: T, rowIndex: number) => void;
  rowActions?: RowAction<T>[];
  toolbarActions?: ToolbarAction[];
  externalUpdateTrigger?: number;
  /** With config.enableRowSelection: the selected rows, in view order, on every change. */
  onSelectionChange?: (rows: T[]) => void;
  /** Called after the table changes its model: a cell edit, a discard, a save. */
  onModelChange?: () => void;
  /** Shown centered in the data area when there are no rows. */
  emptyMessage?: React.ReactNode;
  /**
   * What the table is doing (FEATURE-20260825-table-loading-vs-empty-state):
   * loading — a fetch is in flight: the toolbar's refresh control spins, and
   *   NO empty message (it would be a false statement). There is no second
   *   indicator in the data area: the bar that used to sit under the header
   *   was always too much for what it said (glen, 2026-09-12);
   * gated — waiting for a selection elsewhere: the empty message says so;
   * empty — loaded, zero rows: the empty message;
   * error — the last fetch failed: statusMessage, styled as an error;
   * ready — rows.
   */
  status?: "loading" | "gated" | "empty" | "error" | "ready";
  statusMessage?: React.ReactNode;
  /**
   * A status line in the toolbar while rows are shown: a failed refresh, or a
   * warning that the view runs without some columns. With no rows and an
   * error, statusMessage says it instead.
   */
  statusBanner?: React.ReactNode;
  /**
   * Re-run the view's query. Given one, the toolbar draws a discreet refresh
   * control — so every view that can be re-queried has one without the page
   * asking (FEATURE-20260910-page-dsl-view-polish).
   */
  onRefresh?: () => void;
}

export function Table<T = unknown>({
  model,
  config,
  height = 600,
  width = 800,
  onRowClick,
  rowActions,
  toolbarActions,
  externalUpdateTrigger,
  onSelectionChange,
  onModelChange,
  emptyMessage,
  status,
  statusMessage,
  statusBanner,
  onRefresh,
}: TableProps<T>) {
  const {
    rowHeight = 50,
    headerHeight = 56,
    striped = false,
    hoverable = true,
    showColumnBorders = false,
    showRowBorders = true,
    overscanCount = 5,
    className = "",
    style = {},
    enableSearch = false,
    enableColumnResizing = false,
    enableColumnChooser = false,
    showToolbar = false,
    toolbarHeight = 50,
    editMode = "none",
    enableEditing = false,
    showValidationErrors = true,
    enableChangeTracking = false,
    showSaveButtons = false,
    enableRowSelection = false,
  } = config || {};

  const effectiveEditMode = enableEditing ? "inline" : editMode;
  const effectiveShowSaveButtons = showSaveButtons || enableChangeTracking;

  const [modelVersion, setModelVersion] = useState(0);
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(0);
  const [editingCellValue, setEditingCellValue] = useState<unknown>(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    rowIndex: number;
    column: ColumnDef<T>;
    value: unknown;
  } | null>(null);

  const headerRef = useRef<HTMLDivElement>(null);
  const listOuterRef = useRef<HTMLDivElement>(null);

  const visibleColumns = useMemo(
    () => model.getVisibleColumns(),
    [model.columns, modelVersion, externalUpdateTrigger]
  );

  // Try to get rowActions from BiqlTableModel if not explicitly provided
  const effectiveRowActions = useMemo(() => {
    if (rowActions) return rowActions;
    if (model instanceof BiqlTableModel && model.rowActions) {
      return model.rowActions;
    }
    return undefined;
  }, [rowActions, model]);

  const totalContentWidth = useMemo(() => {
    const columnsWidth = visibleColumns.reduce((sum, col) => sum + (col.width || 150), 0);
    const actionsWidth = effectiveRowActions ? 60 : 0;
    return columnsWidth + actionsWidth + (enableRowSelection ? SELECT_COL_WIDTH : 0);
  }, [visibleColumns, effectiveRowActions, enableRowSelection]);

  // Use search-filtered data if search is enabled, otherwise use regular filtered data
  const sortedData = useMemo(() => {
    return enableSearch
      ? model.getSearchFilteredData()
      : model.getFilteredData();
  }, [model.data, enableSearch, modelVersion, externalUpdateTrigger]);

  const rowCount = sortedData.length;

  const onModelChangeRef = useRef(onModelChange);
  useEffect(() => {
    onModelChangeRef.current = onModelChange;
  }, [onModelChange]);
  useEffect(() => {
    if (modelVersion > 0) onModelChangeRef.current?.();
  }, [modelVersion]);

  // Row selection, by row identity so it survives a sort or a filter. A row that
  // leaves the data (a refresh, a delete) leaves the selection with it.
  const [selection, setSelection] = useState<Set<T>>(() => new Set());
  const lastToggled = useRef<number | null>(null);
  const onSelectionChangeRef = useRef(onSelectionChange);
  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
  }, [onSelectionChange]);
  const selectedInView = useMemo(
    () => (enableRowSelection ? sortedData.filter((r) => selection.has(r)) : []),
    [enableRowSelection, sortedData, selection],
  );
  useEffect(() => {
    if (enableRowSelection) onSelectionChangeRef.current?.(selectedInView);
  }, [enableRowSelection, selectedInView]);
  // Turning selection off drops it: turning it back on starts with nothing selected.
  useEffect(() => {
    if (!enableRowSelection) {
      setSelection((prev) => (prev.size === 0 ? prev : new Set()));
      lastToggled.current = null;
    }
  }, [enableRowSelection]);
  const toggleRow = (index: number, extend: boolean) => {
    setSelection((prev) => {
      const next = new Set(prev);
      const from = extend && lastToggled.current !== null ? lastToggled.current : index;
      const [lo, hi] = from <= index ? [from, index] : [index, from];
      const select = !prev.has(sortedData[index]);
      for (let i = lo; i <= hi; i++) {
        if (select) next.add(sortedData[i]);
        else next.delete(sortedData[i]);
      }
      return next;
    });
    lastToggled.current = index;
  };
  const allSelected = rowCount > 0 && selectedInView.length === rowCount;
  const toggleAll = () => {
    setSelection(allSelected ? new Set() : new Set(sortedData));
    lastToggled.current = null;
  };

  const handleModelChange = useCallback(() => {
    setModelVersion((v) => v + 1);
  }, []);

  const handleSaveClick = useCallback(() => {
    setShowSaveDialog(true);
  }, []);

  // Discard asks on the button itself, not in a browser dialog: the first click arms
  // it ("Click again to discard"), a second click within a few seconds discards.
  // The armed flag is read through a ref: the toolbar can hold an older copy of the
  // handler, and a closure over state would see the first click forever.
  const [discardArmed, setDiscardArmed] = useState(false);
  const discardArmedRef = useRef(false);
  useEffect(() => {
    discardArmedRef.current = discardArmed;
    if (!discardArmed) return;
    const t = setTimeout(() => setDiscardArmed(false), 4000);
    return () => clearTimeout(t);
  }, [discardArmed]);
  const handleDiscardClick = useCallback(() => {
    if (!discardArmedRef.current) {
      discardArmedRef.current = true;
      setDiscardArmed(true);
      return;
    }
    discardArmedRef.current = false;
    setDiscardArmed(false);
    model.discardChanges();
    setModelVersion((v) => v + 1);
  }, [model]);

  const handleSaveChanges = useCallback(async (): Promise<SaveResult> => {
    if (!model.saveChanges || !model.changeTracker) {
      return {
        success: false,
        summary: { inserted: 0, updated: 0, deleted: 0, failed: 0 },
        errors: new Map([["error", "No save handler configured"]]),
      };
    }

    const changes = model.changeTracker.getChangeSet();
    const result = await model.saveChanges(changes);

    if (result.success) {
      if (model.acceptChanges) model.acceptChanges();
      else model.changeTracker.clear();
      setModelVersion((v) => v + 1);
    }

    return result;
  }, [model]);

  const effectiveShowToolbar =
    showToolbar ||
    enableSearch ||
    enableColumnChooser ||
    effectiveShowSaveButtons;

  const effectiveToolbarHeight = effectiveShowToolbar ? toolbarHeight : 0;
  const listHeight = height - headerHeight - effectiveToolbarHeight;

  const handleHeaderClick = (column: ColumnDef<T>) => {
    if (!column.sortable) return;

    const currentSort = model.sortConfig;
    if (currentSort?.columnId === column.id) {
      // Toggle direction or clear sort
      if (currentSort.direction === "asc") {
        model.setSortConfig({ columnId: column.id, direction: "desc" });
      } else {
        model.setSortConfig(undefined);
      }
    } else {
      model.setSortConfig({ columnId: column.id, direction: "asc" });
    }
    setModelVersion((v) => v + 1);
  };

  const getSortIndicator = (column: ColumnDef<T>) => {
    if (!column.sortable) return null;
    const active = model.sortConfig?.columnId === column.id;
    const glyph = !active ? " ↕" : model.sortConfig!.direction === "asc" ? " ↑" : " ↓";
    return (
      <span className={`sort-indicator ${active ? "active" : ""}`}>{glyph}</span>
    );
  };

  const handleResizeStart = useCallback(
    (e: React.MouseEvent, column: ColumnDef<T>) => {
      if (!enableColumnResizing || column.resizable === false) return;
      e.preventDefault();
      e.stopPropagation();
      setResizingColumn(column.id);
      setResizeStartX(e.clientX);
      setResizeStartWidth(column.width);
    },
    [enableColumnResizing]
  );

  const handleResizeMove = useCallback(
    (e: MouseEvent) => {
      if (!resizingColumn) return;
      const deltaX = e.clientX - resizeStartX;
      const column = model.getColumn(resizingColumn);
      if (!column) return;

      const newWidth = Math.max(
        column.minWidth || 50,
        Math.min(column.maxWidth || 1000, resizeStartWidth + deltaX)
      );
      model.updateColumn(resizingColumn, { width: newWidth });
      setModelVersion((v) => v + 1);
    },
    [resizingColumn, resizeStartX, resizeStartWidth, model]
  );

  const handleResizeEnd = useCallback(() => {
    setResizingColumn(null);
  }, []);

  // Add mouse event listeners for resizing
  React.useEffect(() => {
    if (resizingColumn) {
      document.addEventListener("mousemove", handleResizeMove);
      document.addEventListener("mouseup", handleResizeEnd);
      return () => {
        document.removeEventListener("mousemove", handleResizeMove);
        document.removeEventListener("mouseup", handleResizeEnd);
      };
    }
  }, [resizingColumn, handleResizeMove, handleResizeEnd]);

  React.useEffect(() => {
    const el = listOuterRef.current;
    if (!el) return;
    const onScroll = () => {
      if (headerRef.current) {
        headerRef.current.style.transform = `translateX(-${el.scrollLeft}px)`;
      }
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  });

  // react-window renders `innerElementType` and `children` as COMPONENT TYPES,
  // and a new function identity is a NEW TYPE to React: the whole row area
  // unmounts and remounts, taking any open row-actions menu (its own useState)
  // with it. On the Jobs view that happened every 5 seconds — the menu closed
  // before it could be clicked (glen, 2026-09-10,
  // FEATURE-20260910-page-dsl-view-polish). So both are created ONCE, and what
  // they need from the current render reaches them WITHOUT changing their
  // identity: the row renderer through react-window's `itemData`, the content
  // width through a CSS custom property set on the container below.
  const innerElementType = useMemo(
    () => React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
      ({ style: innerStyle, ...rest }, ref) => (
        // Size the row area to the columns' total width so it scrolls
        // horizontally when the columns are wider than the container, but never
        // narrower than the container: the row rules and the selected-row wash
        // span the panel edge to edge, like the header rule above them.
        // (react-window puts width:100% in innerStyle, so the override is inline.)
        <div
          ref={ref}
          style={{ ...innerStyle, width: "var(--dt-content-w, 100%)", minWidth: "100%" }}
          {...rest}
        />
      )
    ),
    []
  );

  const renderHeader = () => {
    return (
      <div style={{ overflow: "hidden", width: "100%" }}>
        <div
          ref={headerRef}
          className="table-header"
          style={{
            height: headerHeight,
            display: "flex",
            // Match the row area width so the header lines up with the rows, and
            // like the rows never narrower than the container.
            width: totalContentWidth,
            minWidth: "100%",
          }}
        >
        {enableRowSelection && (
          <div className="table-header-cell table-select-cell" style={selectCellStyle}>
            <input
              type="checkbox"
              aria-label="Select all rows"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = selectedInView.length > 0 && !allSelected;
              }}
              onChange={toggleAll}
            />
          </div>
        )}
        {visibleColumns.map((column) => (
          <div
            key={column.id}
            className={`table-header-cell ${
              column.sortable ? "sortable" : ""
            } ${
              enableColumnResizing && column.resizable !== false
                ? "resizable"
                : ""
            }`}
            style={{
              width: column.width,
              minWidth: column.minWidth || column.width,
              maxWidth: column.maxWidth,
              flexShrink: 0,
              textAlign: column.align || column.dataType?.align || "left",
              borderRight: showColumnBorders ? "1px solid var(--dt-rule-col)" : "none",
              cursor: column.sortable ? "pointer" : "default",
              display: "flex",
              alignItems: "center",
              padding: "0 12px",
              position: "relative",
              ...column.headerStyle,
            }}
            onClick={() => handleHeaderClick(column)}
          >
            <span style={{ flex: 1 }}>
              {column.headerRenderer
                ? column.headerRenderer(column)
                : column.header}
              {getSortIndicator(column)}
            </span>
            {enableColumnResizing && column.resizable !== false && (
              <div
                className="resize-handle"
                onMouseDown={(e) => handleResizeStart(e, column)}
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: "absolute",
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: "8px",
                  cursor: "col-resize",
                  backgroundColor:
                    resizingColumn === column.id ? "#4a6fa5" : "transparent",
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) => {
                  if (!resizingColumn) {
                    (e.target as HTMLElement).style.backgroundColor = "#555";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!resizingColumn) {
                    (e.target as HTMLElement).style.backgroundColor =
                      "transparent";
                  }
                }}
              />
            )}
          </div>
        ))}
        {effectiveRowActions && (
          <div
            className="table-header-cell"
            style={{
              width: 60,
              minWidth: 60,
              textAlign: "center",
              borderRight: showColumnBorders ? "1px solid var(--dt-rule-col)" : "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 12px",
            }}
          >
            Actions
          </div>
        )}
      </div>
      </div>
    );
  };

  const renderRow: RowRenderer = ({ index, style: rowStyle }) => {
    const row = sortedData[index];
    const isStriped = striped && index % 2 === 1;
    const isSelected = model.selectedRowIndex === index;

    const handleRowClick = () => {
      if (onRowClick) {
        onRowClick(row, index);
      }
    };

    return (
      <div
        className={`table-row ${isStriped ? "striped" : ""} ${
          hoverable ? "hoverable" : ""
        } ${isSelected ? "selected" : ""}`}
        style={{
          ...rowStyle,
          // react-window positions rows absolutely against the OUTER scroller,
          // so its width: 100% is the viewport, not the content: a row narrower
          // than its cells ends its rule and hover/selection wash mid-content
          // once the panel scrolls sideways. Size rows like the inner element.
          width: totalContentWidth,
          minWidth: "100%",
          display: "flex",
          borderBottom: showRowBorders ? "1px solid var(--dt-rule-row)" : "none",
          backgroundColor: isSelected
            ? "var(--dt-selected)"
            : isStriped
            ? "var(--dt-zebra)"
            : "transparent",
          // a staged insert: a green bar down its left edge
          boxShadow: model.changeTracker?.isInsertedRow(row) ? "inset 3px 0 0 #22c55e" : undefined,
          cursor: onRowClick ? "pointer" : "default",
        }}
        onClick={handleRowClick}
      >
        {enableRowSelection && (
          <div
            className="table-cell table-select-cell"
            style={selectCellStyle}
            onClick={(e) => {
              e.stopPropagation();
              toggleRow(index, e.shiftKey);
            }}
          >
            <input type="checkbox" aria-label={`Select row ${index + 1}`} checked={selection.has(row)} readOnly />
          </div>
        )}
        {visibleColumns.map((column) => {
          const value = column.accessor(row);
          const isEditing =
            effectiveEditMode === "inline" &&
            model.editingCell?.rowIndex === index &&
            model.editingCell?.columnId === column.id;
          const isEditable =
            effectiveEditMode === "inline" && column.editable !== false;
          const cellErrors = model.getCellErrors(index, column.id);

          const handleCellDoubleClick = (e: React.MouseEvent) => {
            e.stopPropagation(); // Prevent row click handler from firing
            if (isEditable && !isEditing) {
              model.startEditCell(index, column.id);
              setEditingCellValue(value);
              setModelVersion((v) => v + 1);
            }
          };

          const handleCommit = async () => {
            if (isEditing) {
              const result = await model.commitCellEdit(
                index,
                column.id,
                editingCellValue
              );
              setModelVersion((v) => v + 1);
              if (!result.valid && showValidationErrors) {
                // Errors are stored in model, will be displayed
              }
            }
          };

          const handleCancel = () => {
            if (isEditing) {
              model.cancelEdit();
              setEditingCellValue(null);
              setModelVersion((v) => v + 1);
            }
          };

          // Determine cell content
          let cellContent;
          if (isEditing && column.dataType?.renderEditor) {
            // Show editor; a column that accepts NULL gets a button beside it that flips
            // the cell between NULL and the type's empty value, labelled with what it writes
            const editor = column.dataType.renderEditor(
              editingCellValue,
              setEditingCellValue,
              handleCommit,
              handleCancel
            );
            const flipValue = flipTarget(editingCellValue, column.dataType.emptyValue);
            cellContent =
              column.nullable === false ? (
                editor
              ) : (
                <span style={{ display: "flex", alignItems: "center", gap: 4, width: "100%" }}>
                  <span style={{ flex: 1, minWidth: 0 }}>{editor}</span>
                  <button
                    type="button"
                    className="table-cell-null-button"
                    title={flipValue === null ? "Set this cell to NULL" : `Set this cell to ${emptyValueLabel(flipValue)}`}
                    // keep the input from committing its text on blur first
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={async (e) => {
                      e.stopPropagation();
                      await model.commitCellEdit(index, column.id, flipValue);
                      setEditingCellValue(null);
                      setModelVersion((v) => v + 1);
                    }}
                  >
                    {flipValue === null ? "NULL" : emptyValueLabel(flipValue)}
                  </button>
                </span>
              );
          } else {
            // Show display value
            if (column.cellRenderer) {
              cellContent = column.cellRenderer(value, row, index);
            } else if (column.dataType) {
              cellContent = column.dataType.render(value, index);
            } else {
              cellContent = value != null ? String(value) : "";
            }
          }

          // Determine cell style: merge dataType style with custom style
          const dataTypeStyle =
            column.dataType?.getCellStyle?.(value, index) || {};
          const customStyle = column.cellStyle?.(value, row, index) || {};
          const cellStyle = { ...dataTypeStyle, ...customStyle };

          // A staged edit: an amber wash and bar, the value it replaces on hover
          const cellChange = model.changeTracker?.cellChange(row, column.id);
          if (cellChange) {
            cellStyle.backgroundColor = "rgba(234, 179, 8, 0.16)";
            cellStyle.boxShadow = "inset 2px 0 0 #eab308";
          }

          // Add error styling if there are validation errors
          if (cellErrors && !cellErrors.valid && showValidationErrors) {
            cellStyle.borderColor = "#ff4444";
            cellStyle.borderWidth = "2px";
            cellStyle.borderStyle = "solid";
          }

          return (
            <div
              key={column.id}
              className={`table-cell ${isEditable ? "editable" : ""} ${
                isEditing ? "editing" : ""
              }`}
              style={{
                width: column.width,
                minWidth: column.minWidth || column.width,
                maxWidth: column.maxWidth,
                flexShrink: 0,
                textAlign: column.align || column.dataType?.align || "left",
                borderRight: showColumnBorders ? "1px solid var(--dt-rule-col)" : "none",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "stretch",
                padding: isEditing ? "0" : "0 12px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                cursor: isEditable && !isEditing ? "cell" : "default",
                ...cellStyle,
              }}
              onClick={(e) => {
                // Only swallow the click for editable cells (so a single click on
                // an editable cell doesn't also fire row selection). Read-only
                // cells let the click bubble to the row's onRowClick — otherwise a
                // user clicking cell content (the normal case) could never select
                // or drill into a row.
                if (isEditable) {
                  e.stopPropagation();
                }
              }}
              onDoubleClick={handleCellDoubleClick}
              onContextMenu={(e) => {
                // Show context menu for editable cells
                if (isEditable && !isEditing) {
                  e.preventDefault();
                  setContextMenu({
                    visible: true,
                    x: e.clientX,
                    y: e.clientY,
                    rowIndex: index,
                    column,
                    value,
                  });
                }
              }}
              title={
                cellErrors && !cellErrors.valid
                  ? cellErrors.errors?.join(", ")
                  : cellChange
                  ? `changed; was: ${cellChange.oldValue == null ? "NULL" : String(cellChange.oldValue)}`
                  : undefined
              }
            >
              {cellContent}
              {cellErrors &&
                !cellErrors.valid &&
                showValidationErrors &&
                !isEditing && (
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#ff4444",
                      marginTop: "2px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {cellErrors.errors?.[0]}
                  </div>
                )}
            </div>
          );
        })}
        {effectiveRowActions && (
          <div
            className="table-cell"
            style={{
              width: 60,
              minWidth: 60,
              textAlign: "center",
              borderRight: showColumnBorders ? "1px solid var(--dt-rule-col)" : "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 8px",
            }}
          >
            <RowActionsMenu
              actions={effectiveRowActions}
              row={row}
              rowIndex={index}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div
        className={`table-container ${className}`}
        // display:block is explicit so a host page's broad `div { display: flex }`
        // rule can't turn the container into a flex row — that would lay the header
        // and the virtualized list out side-by-side (header left, data right) and
        // scroll them together. The Table stacks header-over-list in normal flow.
        style={
          {
            display: "block",
            width,
            height,
            // read by innerElementType, which must not change identity (above)
            "--dt-content-w": `${totalContentWidth}px`,
            ...style,
          } as React.CSSProperties
        }
      >
        {effectiveShowToolbar && (
          <TableToolbar
            model={model}
            enableSearch={enableSearch}
            enableColumnChooser={enableColumnChooser}
            showSaveButtons={effectiveShowSaveButtons}
            onModelChange={handleModelChange}
            onSaveClick={handleSaveClick}
            onDiscardClick={handleDiscardClick}
            discardArmed={discardArmed}
            actions={toolbarActions}
            rowCount={rowCount}
            totalCount={model.data.length}
            onRefresh={onRefresh}
            refreshing={status === "loading"}
            statusNode={status === "error" && rowCount === 0 ? undefined : statusBanner}
          />
        )}
        {renderHeader()}
        {rowCount === 0 && status === "error" ? (
          <div
            className="table-empty table-error"
            style={{
              height: listHeight,
              display: "flex",
              // "safe": an expanded error panel taller than the area scrolls
              // instead of overflowing up over the toolbar.
              alignItems: "safe center",
              overflowY: "auto",
              justifyContent: "center",
              padding: "8px 24px",
              textAlign: "center",
            }}
          >
            {statusMessage ?? "The query failed."}
          </div>
        ) : rowCount === 0 && status !== "loading" && (emptyMessage !== undefined || status === "gated" || status === "empty") ? (
          <div
            className="table-empty"
            style={{
              height: listHeight,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "rgba(255, 255, 255, 0.4)",
            }}
          >
            {emptyMessage ?? (status === "gated" ? "Select a row above." : "No rows.")}
          </div>
        ) : (
          <List
            height={listHeight}
            itemCount={rowCount}
            itemSize={rowHeight}
            width={width}
            overscanCount={overscanCount}
            outerRef={listOuterRef}
            innerElementType={innerElementType}
            itemData={renderRow}
          >
            {TableRow}
          </List>
        )}
      </div>

      {model.changeTracker && model.getRowId && (
        <SaveChangesDialog
          isOpen={showSaveDialog}
          changes={model.changeTracker.getChangeSet()}
          onSave={handleSaveChanges}
          onPreview={model.previewChanges ? () => model.previewChanges!(model.changeTracker!.getChangeSet()) : undefined}
          onCancel={() => setShowSaveDialog(false)}
          getRowLabel={(row) => {
            const id = model.getRowId!(row);
            return `Row ${id}`;
          }}
        />
      )}

      {/* Context Menu */}
      {contextMenu && contextMenu.visible && (
        <>
          {/* Backdrop to capture clicks outside */}
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999,
            }}
            onClick={() => setContextMenu(null)}
            onContextMenu={(e) => {
              e.preventDefault();
              setContextMenu(null);
            }}
          />
          {/* Menu */}
          <div
            style={{
              position: "fixed",
              top: contextMenu.y,
              left: contextMenu.x,
              backgroundColor: "#2a2a2a",
              border: "1px solid #555",
              borderRadius: "4px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.5)",
              zIndex: 1000,
              minWidth: "150px",
            }}
          >
            {contextMenu.column.nullable !== false && (() => {
              const flipValue = flipTarget(contextMenu.value, contextMenu.column.dataType?.emptyValue);
              return (
              <div
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  color: "#e0e0e0",
                  fontSize: "14px",
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLElement).style.backgroundColor = "#3a3a3a";
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLElement).style.backgroundColor =
                    "transparent";
                }}
                onClick={() => {
                  model.commitCellEdit(
                    contextMenu.rowIndex,
                    contextMenu.column.id,
                    flipValue
                  );
                  handleModelChange();
                  setContextMenu(null);
                }}
              >
                Set to {flipValue === null ? "NULL" : emptyValueLabel(flipValue)}
              </div>
              );
            })()}
          </div>
        </>
      )}
    </>
  );
}
